import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useChefsLab } from '../../context/ChefsLabContext';

// External link icon
const ExternalLinkIcon = ({ color }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

// Flag icon
const FlagIcon = ({ color }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

// Chevron icon
const ChevronIcon = ({ color, rotate }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    style={{ transform: rotate ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Format price
const formatPrice = (price) => {
  if (price === null || price === undefined) return '--';
  return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'No date';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  } catch {
    return dateStr;
  }
};

// Get score color
const getScoreColor = (score, theme) => {
  if (score >= 90) return '#22c55e'; // green
  if (score >= 70) return '#f59e0b'; // yellow/orange
  return '#ef4444'; // red
};

const ListingCard = ({ listing, onReport }) => {
  const { theme } = useTheme();
  const { isListingReported, getListingReport } = useChefsLab();

  const [expanded, setExpanded] = useState(false);

  const isReported = isListingReported(listing.url);
  const report = getListingReport(listing.url);

  const score = listing.match_score;
  const hasScore = score !== undefined && score !== null;
  const scoreColor = hasScore ? getScoreColor(score, theme) : theme.textMuted;

  const styles = {
    card: {
      backgroundColor: theme.bgCard,
      borderRadius: '10px',
      border: `1px solid ${theme.border}`,
      overflow: 'hidden',
      marginBottom: '8px',
    },
    cardReported: {
      opacity: 0.6,
      border: `1px solid ${theme.warning}`,
    },
    cardFlagged: {
      border: `1px solid ${theme.warning}`,
    },
    cardOutlier: {
      border: `1px solid ${theme.error}`,
    },
    main: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '12px',
      gap: '12px',
      cursor: 'pointer',
    },
    content: {
      flex: 1,
      minWidth: 0,
    },
    title: {
      color: theme.text,
      fontSize: '13px',
      fontWeight: 500,
      margin: '0 0 6px 0',
      lineHeight: 1.3,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
    },
    price: {
      color: '#22c55e',
      fontSize: '15px',
      fontWeight: 700,
    },
    date: {
      color: theme.textMuted,
      fontSize: '12px',
    },
    badges: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginTop: '6px',
      flexWrap: 'wrap',
    },
    badge: {
      fontSize: '10px',
      fontWeight: 600,
      padding: '2px 6px',
      borderRadius: '4px',
      textTransform: 'uppercase',
    },
    companyBadge: {
      backgroundColor: theme.accentBg,
      color: theme.accent,
    },
    gradeBadge: {
      backgroundColor: theme.bgInput,
      color: theme.text,
    },
    scoreBadge: {
      backgroundColor: 'transparent',
      border: `1px solid ${scoreColor}`,
      color: scoreColor,
    },
    flagBadge: {
      backgroundColor: 'rgba(245, 158, 11, 0.15)',
      color: '#f59e0b',
    },
    outlierBadge: {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      color: '#ef4444',
    },
    reportedBadge: {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
      color: '#ef4444',
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '8px',
    },
    linkButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      padding: '6px 10px',
      borderRadius: '8px',
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      border: 'none',
      cursor: 'pointer',
      color: '#0ea5e9',
      fontSize: '11px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    expandButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
    },
    expandedContent: {
      padding: '0 12px 12px',
      borderTop: `1px solid ${theme.border}`,
    },
    reasonsTitle: {
      color: theme.textMuted,
      fontSize: '11px',
      fontWeight: 600,
      textTransform: 'uppercase',
      margin: '12px 0 8px 0',
    },
    reasonsList: {
      margin: 0,
      padding: '0 0 0 16px',
    },
    reasonItem: {
      color: theme.textSecondary,
      fontSize: '12px',
      marginBottom: '4px',
    },
    flagsContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginTop: '8px',
    },
    reportButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 10px',
      borderRadius: '6px',
      backgroundColor: theme.bgInput,
      border: 'none',
      color: theme.textMuted,
      fontSize: '12px',
      cursor: 'pointer',
      marginTop: '12px',
    },
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
    if (listing.url) {
      window.open(listing.url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleReportClick = (e) => {
    e.stopPropagation();
    if (onReport) {
      onReport(listing);
    }
  };

  // Get card style based on status
  const getCardStyle = () => {
    let cardStyle = { ...styles.card };
    if (isReported) cardStyle = { ...cardStyle, ...styles.cardReported };
    else if (listing.flagged || listing.anomalyFlag) cardStyle = { ...cardStyle, ...styles.cardFlagged };
    else if (listing.outlier) cardStyle = { ...cardStyle, ...styles.cardOutlier };
    return cardStyle;
  };

  return (
    <div style={getCardStyle()}>
      <div style={styles.main} onClick={() => setExpanded(!expanded)}>
        <div style={styles.content}>
          <p style={styles.title}>{listing.title || 'Untitled Listing'}</p>

          <div style={styles.meta}>
            <span style={styles.price}>{formatPrice(listing.price)}</span>
            <span style={styles.date}>{formatDate(listing.date)}</span>
          </div>

          <div style={styles.badges}>
            {listing.company && (
              <span style={{ ...styles.badge, ...styles.companyBadge }}>
                {listing.company.toUpperCase()}
              </span>
            )}
            {listing.grade_number && (
              <span style={{ ...styles.badge, ...styles.gradeBadge }}>
                Grade {listing.grade_number}
              </span>
            )}
            {hasScore && (
              <span style={{ ...styles.badge, ...styles.scoreBadge }}>
                {score}%
              </span>
            )}
            {(listing.flagged || listing.anomalyFlag) && (
              <span style={{ ...styles.badge, ...styles.flagBadge }}>
                {listing.anomalyFlag ? 'Anomaly' : 'Flagged'}
              </span>
            )}
            {listing.outlier && (
              <span style={{ ...styles.badge, ...styles.outlierBadge }}>
                Outlier
              </span>
            )}
            {isReported && (
              <span style={{ ...styles.badge, ...styles.reportedBadge }}>
                Reported
              </span>
            )}
          </div>
        </div>

        <div style={styles.actions}>
          <button style={styles.linkButton} onClick={handleLinkClick}>
            View
            <ExternalLinkIcon color="#0ea5e9" />
          </button>
          <button style={styles.expandButton}>
            <ChevronIcon color={theme.textMuted} rotate={expanded} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            style={styles.expandedContent}
          >
            {/* Match Reasons */}
            {listing.match_reasons && listing.match_reasons.length > 0 && (
              <>
                <p style={styles.reasonsTitle}>Match Reasons</p>
                <ul style={styles.reasonsList}>
                  {listing.match_reasons.map((reason, i) => (
                    <li key={i} style={styles.reasonItem}>{reason}</li>
                  ))}
                </ul>
              </>
            )}

            {/* Flags */}
            {listing.flags && Object.keys(listing.flags).length > 0 && (
              <>
                <p style={styles.reasonsTitle}>Flags</p>
                <div style={styles.flagsContainer}>
                  {Object.entries(listing.flags).map(([key, value]) => (
                    value && (
                      <span key={key} style={{ ...styles.badge, ...styles.flagBadge }}>
                        {key.replace(/_/g, ' ')}
                      </span>
                    )
                  ))}
                </div>
              </>
            )}

            {/* Outlier Reason */}
            {listing.outlier && listing.outlier_reason && (
              <>
                <p style={styles.reasonsTitle}>Outlier Reason</p>
                <p style={{ ...styles.reasonItem, paddingLeft: 0, margin: 0 }}>
                  {listing.outlier_reason}
                </p>
              </>
            )}

            {/* Anomaly Reason (from Poketrace listings) */}
            {listing.anomalyFlag && listing.anomalyReason && (
              <>
                <p style={styles.reasonsTitle}>Anomaly</p>
                <p style={{ ...styles.reasonItem, paddingLeft: 0, margin: 0 }}>
                  {listing.anomalyReason}
                </p>
              </>
            )}

            {/* Report Reason (if reported) */}
            {isReported && report && (
              <>
                <p style={styles.reasonsTitle}>Your Report</p>
                <div style={styles.flagsContainer}>
                  {report.reasons?.map((reason, i) => (
                    <span key={i} style={{ ...styles.badge, ...styles.reportedBadge }}>
                      {reason}
                    </span>
                  ))}
                </div>
                {report.notes && (
                  <p style={{ ...styles.reasonItem, marginTop: '8px' }}>
                    "{report.notes}"
                  </p>
                )}
              </>
            )}

            {/* Report Button */}
            {!isReported && (
              <button style={styles.reportButton} onClick={handleReportClick}>
                <FlagIcon color={theme.textMuted} />
                Report Listing
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ListingCard;
